import React, { useState, useEffect, useRef } from 'react';
import { useDirectChat } from '../../hooks/useDirectChat';
import { useChatUI } from '../../context/ChatUIContext';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { api } from '../../utils/api';
import FileViewerModal from '../feature/FileViewerModal';

interface ChatOverlayProps {
  onClose?: () => void;
}

export default function ChatOverlay({ onClose: customOnClose }: ChatOverlayProps) {
  const { activeUser, isOpen, isMinimized, setMinimized, closeChat, openChat } = useChatUI();
  const [view, setView] = useState<'chat' | 'list'>(activeUser ? 'chat' : 'list');

  const {
    messages,
    conversations,
    isTyping,
    isConnected,
    presence,
    markAsRead,
    sendMessage,
    sendTypingStatus,
    userId
  } = useDirectChat(activeUser?.id);

  const [inputText, setInputText] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<{ url: string, name: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Synchroniser la vue quand l'utilisateur change globalement
  useEffect(() => {
    if (activeUser) setView('chat');
  }, [activeUser]);

  // Auto-scrolling à chaque nouveau message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom();
    // Marquer comme lu à chaque réception de message si la vue est 'chat'
    if (view === 'chat' && !isMinimized && messages.some(m => !m.isRead && m.receiverId === userId)) {
      markAsRead();
    }
  }, [messages, isTyping, view, isMinimized, markAsRead, userId, isOpen]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    sendMessage(inputText.trim());
    setInputText('');
    sendTypingStatus(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    sendTypingStatus(e.target.value.length > 0);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeUser?.id) return;
    
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/upload/image', formData); 
      const fileUrl = uploadRes.url || (uploadRes.data && uploadRes.data.url) || uploadRes;
      
      sendMessage('', [{
        url: typeof fileUrl === 'string' ? fileUrl : fileUrl.url,
        name: file.name,
        type: file.type.startsWith('image/') ? 'IMAGE' : 'DOCUMENT',
        size: file.size
      }]);
    } catch (err: any) {
      console.error('Upload error:', err);
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  // Group messages by date
  const groupedMessages = (messages || []).reduce((groups: { [key: string]: typeof messages }, msg) => {
    const date = new Date(msg.createdAt).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
    const today = new Date().toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
    const label = date === today ? "Aujourd'hui" : date;

    if (!groups[label]) groups[label] = [];
    groups[label].push(msg);
    return groups;
  }, {});

  return (
    <div className={`fixed bottom-4 right-4 sm:right-24 z-[999999] w-full max-w-[340px] transition-all duration-500 ease-in-out ${isMinimized ? 'h-[64px]' : 'h-[500px]'
      }`}>
      <div className={`bg-white rounded-[2rem] shadow-2xl border border-gray-100 flex flex-col h-full overflow-hidden transition-all duration-500 ${isMinimized ? 'rounded-full' : 'rounded-t-[2rem]'
        }`}>

        {/* Header */}
        <div
          onClick={() => setMinimized(!isMinimized)}
          className={`bg-[#1B3A2F] p-4 flex items-center justify-between text-white cursor-pointer hover:bg-[#1B3A2F]/95 transition-all ${isMinimized ? 'h-full' : ''
            }`}
        >
          <div className="flex items-center gap-3">
            {view === 'chat' && !isMinimized && (
              <button
                onClick={(e) => { e.stopPropagation(); setView('list'); }}
                className="w-8 h-8 -ml-2 flex items-center justify-center rounded-full hover:bg-white/10"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
            )}
            <div className="relative">
              <div className="w-10 h-10 rounded-full border-2 border-white/20 overflow-hidden bg-white/10 flex items-center justify-center">
                {activeUser?.photoProfil ? (
                  <img src={activeUser.photoProfil} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-black text-sm">{activeUser?.prenom?.[0] || 'M'}</span>
                )}
              </div>
              <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[#1B3A2F] ${view === 'chat'
                  ? (presence?.status === 'online' ? 'bg-emerald-400' : 'bg-gray-400')
                  : (isConnected ? 'bg-emerald-400' : 'bg-rose-400')
                }`}></div>
            </div>
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-widest">
                {view === 'chat' ? `${activeUser?.prenom} ${activeUser?.nom}` : 'Messagerie'}
              </h3>
              <p className="text-[8px] text-emerald-300 font-bold uppercase tracking-tighter">
                {isMinimized ? 'Chat ouvert' : (
                  view === 'chat'
                    ? (presence?.status === 'online' ? 'En ligne' : (presence?.lastSeen ? `En ligne il y a ${formatDistanceToNow(new Date(presence.lastSeen), { addSuffix: false, locale: fr })}` : 'Hors ligne'))
                    : (isConnected ? `${conversations.length} conversation(s)` : 'Connexion...')
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); setMinimized(!isMinimized); }}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className={`${isMinimized ? 'ri-arrow-up-s-line' : 'ri-subtract-line'} text-xl`}></i>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); closeChat(); customOnClose?.(); }}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {view === 'list' ? (
              <div className="flex-1 overflow-y-auto bg-white no-scrollbar">
                {conversations.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center p-8 text-center opacity-40">
                    <i className="ri-chat-smile-3-line text-4xl mb-3 text-[#1B3A2F]"></i>
                    <p className="text-xs font-black uppercase tracking-widest text-[#1B3A2F]">Aucune discussion</p>
                    <p className="text-[10px] font-medium text-gray-600 mt-2">Cliquez sur un collaborateur dans l'annuaire pour commencer.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {conversations.map((conv) => (
                      <div
                        key={conv.user.id}
                        onClick={() => { openChat(conv.user); setView('chat'); }}
                        className="p-4 flex items-center gap-3 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="relative shrink-0">
                          <div className="w-12 h-12 rounded-2xl overflow-hidden bg-gray-100 border border-gray-100">
                            {conv.user.photoProfil ? (
                              <img src={conv.user.photoProfil} className="w-full h-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-sm font-black text-gray-600">
                                {conv.user.prenom?.[0]}
                              </div>
                            )}
                          </div>
                          {conv.unreadCount > 0 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#FFD700] text-white text-[10px] font-black flex items-center justify-center rounded-full border-2 border-white">
                              {conv.unreadCount}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-baseline mb-0.5">
                            <h4 className="text-[11px] font-black uppercase tracking-wider text-[#1B3A2F] truncate">
                              {conv.user.prenom} {conv.user.nom}
                            </h4>
                            <span className="text-[9px] text-gray-500 font-bold shrink-0">
                              {formatDistanceToNow(new Date(conv.lastMessage.createdAt), { addSuffix: false, locale: fr })}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-500 font-medium truncate">
                            {conv.lastMessage.senderId === userId ? 'Vous : ' : ''}
                            {conv.lastMessage.content}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Messages Body */}
                <div className="flex-1 overflow-y-auto p-4 space-y-6 no-scrollbar bg-gray-50/50">
                  {Object.entries(groupedMessages).map(([date, msgs]) => (
                    <div key={date} className="space-y-4">
                      <div className="flex justify-center sticky top-0 z-10 py-2">
                        <span className="text-[8px] font-black text-gray-600 uppercase tracking-widest bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full border border-gray-100 shadow-sm">
                          {date}
                        </span>
                      </div>

                      <AnimatePresence initial={false}>
                        {msgs.map((msg, idx) => {
                          const isMe = msg.senderId === userId;
                          return (
                            <motion.div
                              key={msg.id || idx}
                              initial={{ opacity: 0, y: 10, scale: 0.95 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[85%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                  <div className={`px-4 py-2.5 rounded-2xl text-[13px] leading-relaxed shadow-sm ${isMe
                                      ? 'bg-[#1B3A2F] text-white rounded-tr-none'
                                      : 'bg-white text-[#1B3A2F] border border-gray-100 rounded-tl-none font-medium'
                                    }`}>
                                    {msg.content && <div>{msg.content}</div>}
                                    
                                    {msg.attachments && msg.attachments.length > 0 && (
                                      <div className="mt-2 space-y-2">
                                        {msg.attachments.map((att: any) => (
                                          <div 
                                            key={att.id}
                                            onClick={() => setSelectedAttachment({ url: att.url, name: att.name })}
                                            className={`flex items-center gap-2 p-2 rounded-xl cursor-pointer transition-all ${
                                              isMe ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-50 hover:bg-gray-100'
                                            }`}
                                          >
                                            {att.type === 'IMAGE' ? (
                                              <div className="w-10 h-10 rounded-lg overflow-hidden border border-white/20">
                                                <img src={att.url} alt="" className="w-full h-full object-cover" />
                                              </div>
                                            ) : (
                                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isMe ? 'bg-white/20' : 'bg-white border border-gray-100'}`}>
                                                <i className="ri-file-text-line text-xl"></i>
                                              </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <p className={`text-[10px] font-black uppercase truncate ${isMe ? 'text-white' : 'text-[#1B3A2F]'}`}>{att.name}</p>
                                              <p className={`text-[8px] opacity-60 font-bold ${isMe ? 'text-white' : 'text-gray-500'}`}>
                                                {att.size ? `${(att.size / 1024).toFixed(1)} KB` : att.type}
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    <div className={`text-[8px] mt-1 flex items-center justify-end gap-1 font-black uppercase opacity-60 ${isMe ? 'text-emerald-300' : 'text-gray-600'}`}>
                                      <span className="italic">{new Date(msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                                      {isMe && (
                                        <i className={`ri-check-double-line text-[11px] ${msg.isRead ? 'text-[#FFD700] opacity-100' : 'opacity-40'}`}></i>
                                      )}
                                    </div>
                                  </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  ))}

                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-white p-3 rounded-2xl rounded-tl-none border border-gray-100 shadow-sm flex items-center gap-1.5">
                        <span className="text-[10px] font-bold text-gray-600 mr-1 italic">Saisit...</span>
                        <div className="w-1.5 h-1.5 bg-[#FFD700]/40 rounded-full animate-bounce"></div>
                        <div className="w-1.5 h-1.5 bg-[#FFD700]/40 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                        <div className="w-1.5 h-1.5 bg-[#FFD700]/40 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input area */}
                <div className="p-4 bg-white border-t border-gray-100">
                  <form onSubmit={handleSend} className="flex items-center gap-2">
                    <input 
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadLoading}
                      className="w-10 h-10 flex items-center justify-center bg-gray-50 text-gray-500 rounded-2xl hover:bg-gray-100 transition-all cursor-pointer"
                    >
                      {uploadLoading ? (
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                      ) : (
                        <i className="ri-add-line text-xl"></i>
                      )}
                    </button>
                    <input
                      type="text"
                      value={inputText}
                      onChange={handleInputChange}
                      onBlur={() => sendTypingStatus(false)}
                      placeholder="Écrivez un message..."
                      className="flex-1 bg-gray-50 border-none rounded-2xl px-4 py-3 text-xs font-bold focus:ring-2 focus:ring-[#1B3A2F]/10 transition-all placeholder:text-gray-500"
                    />
                    <button
                      type="submit"
                      disabled={!inputText.trim() && !uploadLoading}
                      className="w-10 h-10 flex items-center justify-center bg-[#1B3A2F] text-white rounded-2xl hover:bg-[#FFD700] disabled:opacity-30 disabled:hover:bg-[#1B3A2F] transition-all cursor-pointer active:scale-90 shadow-lg shadow-[#1B3A2F]/10"
                    >
                      <i className="ri-send-plane-2-line text-lg"></i>
                    </button>
                  </form>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {selectedAttachment && (
        <FileViewerModal 
          url={selectedAttachment.url} 
          nom={selectedAttachment.name} 
          onClose={() => setSelectedAttachment(null)} 
        />
      )}
    </div>
  );
}
